import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import { RootState } from 'store/store';
import { selectCurrentSystemRole } from 'features/application/selectors';
import { selectIsRecorderSession } from 'features/recorder/recorderSlice';
import { LayoutMode } from 'features/layout/types';
import { UserId } from 'features/users/types';
import { selectUserEntries } from 'features/users/usersSlice';

export const selectSystemSessionLayoutMode = createSelector(
  [
    selectCurrentSystemRole,
    (state: RootState) => state.layout.config.recordingMode,
    (state: RootState) => state.layout.config.restreamingMode,
  ],
  (systemRole, recordingMode, restreamingMode): LayoutMode | null => {
    if (systemRole === 'recorder') {
      return recordingMode;
    }
    if (systemRole === 'restreamer') {
      return restreamingMode;
    }
    return null;
  }
);

export const selectShowParticipantNamesInSystemSession = createSelector(
  [
    selectCurrentSystemRole,
    (state: RootState) => state.layout.config.participantNamesInRecordingsEnabled,
    (state: RootState) => state.layout.config.participantNamesInRestreamingEnabled,
  ],
  (systemRole, recordingNames, restreamingNames): boolean => {
    if (systemRole === 'recorder') {
      return recordingNames;
    }
    if (systemRole === 'restreamer') {
      return restreamingNames;
    }
    return true;
  }
);

const selectHideTilesInRecordingsRoles = (state: RootState) =>
  state.layout.config.hideTilesInRecordingsRoles;

export const selectIsHidingRolesInRecording = createSelector(
  [selectIsRecorderSession, selectHideTilesInRecordingsRoles],
  (isRecorderSession, roles): boolean => isRecorderSession && roles.length > 0
);

const NO_HIDDEN_USERS: UserId[] = [];

// `byId` is rewritten on hand raises, permission grants and waiting-room churn; the equality check
// keeps `selectFilteredStreams` and the pagination pipeline quiet until the hidden set really changes.
export const selectRecordingHiddenUserIds = createSelector(
  [selectIsHidingRolesInRecording, selectHideTilesInRecordingsRoles, selectUserEntries],
  (hidingRoles, roles, users): UserId[] => {
    if (!hidingRoles) {
      return NO_HIDDEN_USERS;
    }

    return Object.values(users)
      .filter((user) => roles.includes(user.role))
      .map((user) => user.id);
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } }
);

export const selectHideTilesInRestreaming = createSelector(
  [
    selectCurrentSystemRole,
    (state: RootState) => state.layout.config.hideTilesInRestreamingEnabled,
  ],
  (systemRole, restreamingHide): boolean => systemRole === 'restreamer' && restreamingHide
);

export const selectShowCaptionsInSystemSession = createSelector(
  [selectCurrentSystemRole, (state: RootState) => state.layout.config.captionsInRecordingsEnabled],
  (systemRole, captionsEnabled): boolean => {
    if (systemRole === 'recorder') {
      return captionsEnabled;
    }
    // Restreamer doesn't support captions
    return false;
  }
);
